import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SEO from '../../components/SEO';
import SettingsSidebar from './components/SettingsSidebar';
import ProfileSection from './components/ProfileSection';
import SecuritySection from './components/SecuritySection';
import BillingSection from './components/BillingSection';
import PromocodeRedeem from '../../components/PromocodeRedeem';
import Button from '../../components/ui/Button';

const AccountSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('profile');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Initialize active section from URL parameter
  useEffect(() => {
    const section = searchParams.get('section');
    if (section && ['profile', 'security', 'billing', 'promocode'].includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Redirect to login if user is not authenticated (with redirect so they return here after login)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth/login?redirect=' + encodeURIComponent('/account-settings'));
    }
  }, [user, authLoading, navigate]);

  // Update URL when section changes
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSearchParams({ section });
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection />;
      case 'security':
        return <SecuritySection />;
      case 'billing':
        return <BillingSection />;
      case 'promocode':
        return <PromocodeRedeem />;
      default:
        return <ProfileSection />;
    }
  };

  const getSectionTitle = () => {
    const titles = {
      profile: 'Profile Settings',
      security: 'Security Settings',
      billing: 'Billing & Subscription',
      promocode: 'Promocode Redemption'
    };
    return titles[activeSection] || 'Account Settings';
  };

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Show authentication prompt if user is not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-text-secondary mb-4">Please sign in to access your account settings</p>
          <Button onClick={() => navigate('/auth/login?redirect=' + encodeURIComponent('/account-settings'))}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Account Settings"
        description="Manage your EmailValidator account settings, profile information, security preferences, billing details, and account preferences."
        keywords="account settings, profile management, security settings, billing settings, account preferences, user dashboard, account management"
        canonical="https://mailvalidator.octalchip.com/account-settings"
      />
      <Header />
      
      <div className="mx-auto px-6 py-8">
        <Breadcrumb />
        
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text-primary">Account Settings</h1>
          <p className="text-text-secondary mt-2">
            Manage your account preferences, security settings, and billing information
          </p>
        </div>

        {/* Settings Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3">
            <div className="sticky top-24">
              <SettingsSidebar 
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            <div className="bg-surface rounded-lg border border-border p-6">
              {renderActiveSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;